// Benepack OMS · AuthProvider
// JWT in localStorage · 启动时 hydrate · login/logout · permissions 投射给 PermGuard
//
// 2FA WP: 密码校验通过后, 若需二次验证则【不】持久化 token, 而是把 pre_2fa_token 仅放内存
//   (preTwoFactor·永不进 localStorage), login() 返回 LoginOutcome 让登录页据此跳转
//   /login/verify-2fa (已绑定) 或 /login/enroll-2fa (未绑定+强制)。只有 VerifyTwoFactor 拿到
//   真 token 后才持久化 + 拉 /me 构 AuthUser。pre-2FA 态不跨刷新存活 (内存 only · 刻意)。
import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import * as identityApi from '@/api/identity';
import { TOKEN_KEY, REFRESH_KEY, USER_KEY } from '@/api/client';
import type { AuthUser, VerifyTwoFactorResponse } from '@/api/identity';

/** 登录第一步结果 (密码校验通过后)。 */
export type LoginOutcome =
  | { status: 'authenticated'; user: AuthUser } // 完整登录 (无 2FA / 未强制) · token 已持久化
  | { status: 'two_factor_required' } // 已绑定 2FA → 前端跳 /login/verify-2fa
  | { status: 'must_enroll_2fa' }; // 未绑定 + 强制 → 前端跳 /login/enroll-2fa

/** 内存态: 二步登录中转 (pre_2fa_token 永不进 localStorage)。 */
interface PendingTwoFactor {
  preToken: string;
  mode: 'verify' | 'enroll';
}

export interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  /** 登录第一步: 密码校验。返回 outcome 让登录页决定跳转 (2FA) 或直接进应用。 */
  login: (username: string, password: string) => Promise<LoginOutcome>;
  logout: () => Promise<void>;
  hasPerm: (code: string) => boolean;
  hasAnyPerm: (codes: string[]) => boolean;
  /** 内存中的 pre-2FA 中转态 (二步登录第二步 / 强制 enroll 页读取)。null = 无进行中的两步登录。 */
  pendingTwoFactor: PendingTwoFactor | null;
  /** 二步登录第二步: 用 code (TOTP 或恢复码) 换真 token → 持久化 + 拉 /me 构 user。 */
  verifyTwoFactor: (code: string) => Promise<AuthUser>;
  /** 清内存 pre-2FA 态 (用户放弃二步登录 / 返回登录页)。 */
  clearPendingTwoFactor: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  // ★ pre-2FA 中转态仅内存 (永不 localStorage) · 刷新即失效 (须重新密码登录)。
  const [pendingTwoFactor, setPendingTwoFactor] = useState<PendingTwoFactor | null>(null);

  // 启动时从 localStorage hydrate
  useEffect(() => {
    const raw = localStorage.getItem(USER_KEY);
    if (raw && localStorage.getItem(TOKEN_KEY)) {
      try {
        setUser(JSON.parse(raw) as AuthUser);
      } catch {
        localStorage.removeItem(USER_KEY);
      }
    }
    setLoading(false);
  }, []);

  // persistTokensAndLoadUser: 拿到真 token (登录或 VerifyTwoFactor) 后持久化 + 拉 /me 合并 JWT 构 user。
  // hotfix #2: backend LoginResponse 不返 user 对象 · /me 返 PascalCase 不含 permissions/roles
  //   → 合并 /me (展示字段) + JWT decode (permissions/role_ids)。
  const persistTokensAndLoadUser = useCallback(
    async (accessToken: string, refreshToken: string): Promise<AuthUser> => {
      localStorage.setItem(TOKEN_KEY, accessToken);
      localStorage.setItem(REFRESH_KEY, refreshToken);
      const profile = await identityApi.me();
      const u = identityApi.buildAuthUserFromTokenAndProfile(accessToken, profile);
      localStorage.setItem(USER_KEY, JSON.stringify(u));
      setUser(u);
      return u;
    },
    [],
  );

  const login = useCallback(
    async (username: string, password: string): Promise<LoginOutcome> => {
      const resp = await identityApi.login({ username, password });
      // 2FA 分支: 密码通过但需二次验证 → 不持久化 token, 仅把 pre_2fa_token 放内存。
      if (resp.two_factor_required && resp.pre_2fa_token) {
        setPendingTwoFactor({ preToken: resp.pre_2fa_token, mode: 'verify' });
        return { status: 'two_factor_required' };
      }
      if (resp.must_enroll_2fa && resp.pre_2fa_token) {
        setPendingTwoFactor({ preToken: resp.pre_2fa_token, mode: 'enroll' });
        return { status: 'must_enroll_2fa' };
      }
      // 无 2FA / 未强制 → 完整登录 (token 已颁)。
      const u = await persistTokensAndLoadUser(resp.access_token, resp.refresh_token);
      return { status: 'authenticated', user: u };
    },
    [persistTokensAndLoadUser],
  );

  const verifyTwoFactor = useCallback(
    async (code: string): Promise<AuthUser> => {
      if (!pendingTwoFactor) {
        throw new Error('no pending two-factor session');
      }
      const resp: VerifyTwoFactorResponse = await identityApi.verifyTwoFactor(
        pendingTwoFactor.preToken,
        code,
      );
      const u = await persistTokensAndLoadUser(resp.access_token, resp.refresh_token);
      setPendingTwoFactor(null); // 用完即清内存中转态
      return u;
    },
    [pendingTwoFactor, persistTokensAndLoadUser],
  );

  const clearPendingTwoFactor = useCallback(() => setPendingTwoFactor(null), []);

  const logout = useCallback(async () => {
    try {
      await identityApi.logout();
    } catch {
      // 即便 logout endpoint 失败也清本地态 · 不阻塞用户
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    setPendingTwoFactor(null);
  }, []);

  const hasPerm = useCallback((code: string) => !!user?.permissions?.includes(code), [user]);

  const hasAnyPerm = useCallback(
    (codes: string[]) => codes.some((c) => user?.permissions?.includes(c)),
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      login,
      logout,
      hasPerm,
      hasAnyPerm,
      pendingTwoFactor,
      verifyTwoFactor,
      clearPendingTwoFactor,
    }),
    [
      user,
      loading,
      login,
      logout,
      hasPerm,
      hasAnyPerm,
      pendingTwoFactor,
      verifyTwoFactor,
      clearPendingTwoFactor,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
